import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { DsModalService } from '@design-system/ds-base-modal/services/ds-modal.service';
import { DsModalConfig } from '@design-system/ds-base-modal/interfaces/ds-modal-config.interface';
import { DsConfirmationModalComponent } from '../component/ds-confirmation-modal.component';
import { BaseModalSettingsInterface } from '@interfaces/base-modal-settings.interface';

/**
 * Service for opening the design-system confirmation modal.
 */
@Injectable()
export class DsConfirmationModalService {
  private readonly dsModalService = inject(DsModalService);

  /**
   * Opens a confirmation modal and returns the user's decision on close.
   *
   * @param modalSettings Content and behavior settings passed to the modal component.
   * @param theme Visual theme applied to the modal. Defaults to `'info'`.
   * @param config Optional modal configuration merged with `modalSettings` as dialog data.
   * @returns An observable that emits `true`, `false`, or `undefined` when the modal closes.
   */
  openModal$(
    modalSettings: BaseModalSettingsInterface,
    theme: 'info' | 'success' | 'warning' | 'error' = 'info',
    config?: Omit<DsModalConfig<BaseModalSettingsInterface>, 'data'>,
  ): Observable<boolean | undefined> {
    const dialogRef = this.dsModalService.open<DsConfirmationModalComponent, BaseModalSettingsInterface, boolean>(
      DsConfirmationModalComponent,
      {
        ...config,
        data: modalSettings,
      },
    );

    if (theme) {
      dialogRef.componentInstance.theme = theme;
    }

    if (modalSettings) {
      dialogRef.componentInstance.modalSettings = modalSettings;
    }

    return dialogRef.afterClosed();
  }
}
